import './App.css';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import HomeRedirect from './pages/home/home-redirect';
import ErrorPage from './error-page';
import DsnList from './pages/dsn-list/dsn-list';
import UserAccount from './pages/user-account/user-account';
import Login from './pages/login/login';
import Callback from './pages/callback';
import KeyAuth from './pages/key-auth/KeyAuth';
import CarbonProvider from 'carbon-react/esm/components/carbon-provider';
import GlobalStyle from 'carbon-react/esm/style/global-style';
import sageTheme from 'carbon-react/esm/style/themes/sage';
import TokensWrapper from 'carbon-react/esm/components/tokens-wrapper';
import Auth0Provider from './auth/Auth0Provider';
import { PrivateMainLayout } from './layouts/PrivateMainLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { LoggerProvider } from './context/LoggerContext';
import { UserInfoProvider } from './context/UserInfoQueryContext';
import { AuthAppProvider } from './context/AuthAppContext';
import RoleBasedRoute from './components/RoleBasedRoute';
import Unauthorized from './pages/unauthorized';
import AnonymousRoute from './components/AnonymousRoute';
import { AppRoutes } from './types/appRoutes';
import DsnDetails from './pages/detail/dsn-details';
import BilanDetail from './pages/bilan-detail/bilan-detail';
import Admin from './pages/admin/admin';
import { type JSX, useEffect } from 'react';
import DebugInfos from './pages/debug-infos';
import DebugErrorTest from './pages/debug-error-test';
import { ENV } from './config/env';
import { PrivateNavBarLayout } from './layouts/PrivateNavBarLayout';
import Logout from './pages/login/logout';
import { RootErrorBoundary } from './layouts/ErrorBoundary/RootErrorBoundary';
import SessionTransferHandler from './components/SessionTransferHandler';

const App = (): JSX.Element => {
  useEffect(() => {
    if (ENV.DEBUG_MODE) {
      console.log(`Environment mode is ${process.env.NODE_ENV}.`);
      console.log(`App started in ${ENV.APP_ENV} environment.`);
      console.log(`Layout error mode is ${ENV.LAYOUT_ERROR}.`);
    }
  }, []);

  return (
    <LoggerProvider namespace="App">
      <CarbonProvider theme={sageTheme} validationRedesignOptIn>
        <GlobalStyle />
        <TokensWrapper height="100%">
          <RootErrorBoundary>
            <Auth0Provider>
              <AuthAppProvider>
                <SessionTransferHandler>
                  <Routes>
                    <Route
                      element={
                        <PublicLayout withErrorBoundary={ENV.LAYOUT_ERROR}>
                          <Outlet />
                        </PublicLayout>
                      }
                    >
                      <Route
                        path={AppRoutes.routeLogin}
                        element={
                          <AnonymousRoute>
                            <Login />
                          </AnonymousRoute>
                        }
                      />
                      <Route path={AppRoutes.routeCallback} element={<Callback />} />
                      <Route path={AppRoutes.routeKeyAuth} element={<KeyAuth />} />
                      {ENV.DEBUG_MODE && (
                        <Route path={AppRoutes.routeDebugInfos} element={<DebugInfos />} />
                      )}
                      {ENV.DEBUG_MODE && (
                        <Route
                          path={AppRoutes.routeDebugErrorTestPublic}
                          element={<DebugErrorTest />}
                        />
                      )}
                    </Route>

                    <Route
                      element={
                        <UserInfoProvider>
                          <Outlet />
                        </UserInfoProvider>
                      }
                    >
                      <Route
                        element={
                          <PrivateMainLayout withErrorBoundary={ENV.LAYOUT_ERROR}>
                            <Outlet />
                          </PrivateMainLayout>
                        }
                      >
                        <Route
                          element={
                            <PrivateNavBarLayout withErrorBoundary={ENV.LAYOUT_ERROR}>
                              <Outlet />
                            </PrivateNavBarLayout>
                          }
                        >
                          <Route path={AppRoutes.routeHome} element={<HomeRedirect />} />
                          <Route
                            path={AppRoutes.routeDsnList}
                            element={
                              <RoleBasedRoute notAllowedRoles={['exploit']}>
                                <DsnList />
                              </RoleBasedRoute>
                            }
                          />
                          <Route
                            path={`${AppRoutes.routeDsnDetail}/:tab?`}
                            element={
                              <RoleBasedRoute notAllowedRoles={['exploit']}>
                                <DsnDetails />
                              </RoleBasedRoute>
                            }
                          />
                          <Route
                            path={`${AppRoutes.routeUserAccount}/:tab?`}
                            element={
                              <RoleBasedRoute>
                                <UserAccount />
                              </RoleBasedRoute>
                            }
                          />
                          <Route
                            path={`${AppRoutes.routeAdmin}/:tab?`}
                            element={
                              <RoleBasedRoute allowedRoles={['admin']}>
                                <Admin />
                              </RoleBasedRoute>
                            }
                          />
                          {ENV.DEBUG_MODE && (
                            <Route
                              path={AppRoutes.routeDebugErrorTest}
                              element={<DebugErrorTest />}
                            />
                          )}
                          <Route path={AppRoutes.routeUnauthorized} element={<Unauthorized />} />
                        </Route>
                        <Route path={AppRoutes.routeErrorPage} element={<ErrorPage />} />
                      </Route>
                      {/* Route hors layout applicatif (pas de navbar/footer) : le bilan est un
                          document externe, sa page doit rester agnostique du look de l'app. */}
                      <Route
                        path={AppRoutes.routeBilanDetail}
                        element={
                          <RoleBasedRoute notAllowedRoles={['exploit']}>
                            <BilanDetail />
                          </RoleBasedRoute>
                        }
                      />
                      <Route path={AppRoutes.routeLogout} element={<Logout />} />
                    </Route>
                    <Route path="*" element={<Navigate to={AppRoutes.routeErrorPage} />} />
                  </Routes>
                </SessionTransferHandler>
              </AuthAppProvider>
            </Auth0Provider>
          </RootErrorBoundary>
        </TokensWrapper>
      </CarbonProvider>
    </LoggerProvider>
  );
};

export default App;
